import { axiosInstance } from "@/lib/axios";
import type { NotificationResponse, MarkAsSeenResponse } from "./types";

export type GetNotificationsParams = {
  page?: number;
  /** Page size — API default is 15. */
  limit?: number;
  /** ISO date (YYYY-MM-DD) lower bound. */
  from?: string | null;
  /** ISO date (YYYY-MM-DD) upper bound. */
  to?: string | null;
};

export async function getNotifications(
  params: number | GetNotificationsParams = 1,
): Promise<NotificationResponse> {
  // Back-compat: a bare number is still accepted as the page.
  const { page = 1, limit, from, to } =
    typeof params === "number" ? { page: params } as GetNotificationsParams : params;

  const query = new URLSearchParams();
  query.set("page", String(page));
  if (limit != null) query.set("limit", String(limit));
  if (from) query.set("from", from);
  if (to) query.set("to", to);

  const { data } = await axiosInstance.get<NotificationResponse>(`/notifications?${query.toString()}`);
  return data;
}

export async function markNotificationAsSeen(id: number): Promise<MarkAsSeenResponse> {
  const { data } = await axiosInstance.get<MarkAsSeenResponse>(`/notifications/${id}/seen`);
  return data;
}

/**
 * Marks every given notification seen. The API marks one at a time (`/notifications/{id}/seen`) and has no
 * «all» route, so these are the unread ones the reader has in front of them, four requests at a time. Resolves
 * to how many failed — the rest are seen either way. (A backend «mark all» would replace only this body.)
 */
export async function markNotificationsAsSeen(ids: number[]): Promise<number> {
  let failed = 0;
  for (let i = 0; i < ids.length; i += 4) {
    const batch = await Promise.allSettled(ids.slice(i, i + 4).map((id) => markNotificationAsSeen(id)));
    failed += batch.filter((r) => r.status === "rejected").length;
  }
  return failed;
}

/** Toggles whether the authenticated user receives push notifications. Defaults to true at signup. */
export async function togglePushNotifications(sendNotify: boolean): Promise<any> {
  const { data } = await axiosInstance.post("/send-notify", { send_notify: sendNotify });
  return data;
}
